export const featurePathKey = (path) => JSON.stringify(path);
export const featureDisplayPathKey = (item) => featurePathKey(item.displayPath || item.path);

// Names verified against FeatureFlagService consumers in envana-catalyst.
// Catalyst currently resolves flags by name anywhere in the configuration JSON,
// rather than by their full tree path.
const CATALYST_DYNAMIC_FEATURE_NAMES = new Set([
  "Dashboard",
  "Profiles",
  "Footprints",
  "OGMP 2.0",
  "Data",
  "Reduction",
  "Forecasting",
  "OGMP 2.0 Profiles",
  "My Profiles",
  "Envana Library",
  "Home",
  "Summary",
  "Monthly Breakdown",
  "Annual Breakdown",
  "Analysis",
  "Malaysia Breakdown",
  "JDA",
  "Annual Overview",
  "Export Estimates (.xlsx)",
  "All Emissions & Production (.xlsx)",
  "MBSOC (.xlsx)",
  "MBLEQ (.xlsx)",
  "Footprints Export (.xlsx)",
  "Emissions Export (.xlsx)",
  "Upload Data",
  "EVA Enabled"
]);

export const CATALYST_SUPPORT = {
  SUPPORTED: "supported",
  INDIRECT: "indirect",
  UNSUPPORTED: "unsupported"
};

export const createFeatureState = (items = [], valueField = "enabled") =>
  items.reduce((state, item) => {
    state[featurePathKey(item.path)] = Boolean(item[valueField]);
    return state;
  }, {});

export const createCatalystFeatureHierarchy = (items = []) => {
  const result = [];
  const usedPaths = new Set();
  const mainRouters = items.find((item) => item.path.length === 1 && item.name === "Main Routers");
  if (!mainRouters) {
    return items.map((item) => ({ ...item, displayPath: item.path }));
  }

  const pathStartsWith = (path, prefix) => prefix.length <= path.length && prefix.every((segment, index) => segment === path[index]);
  const appendSubtree = (root, parentDisplayPath, relationshipNote, excludedRoots = []) => {
    if (!root) {
      return;
    }
    items
      .filter(
        (item) =>
          pathStartsWith(item.path, root.path) && !excludedRoots.some((excludedRoot) => pathStartsWith(item.path, excludedRoot.path))
      )
      .forEach((item) => {
        const displayPath = [...parentDisplayPath, root.name, ...item.path.slice(root.path.length)];
        result.push({
          ...item,
          displayPath,
          relationshipNote: item === root ? relationshipNote : null
        });
        usedPaths.add(featurePathKey(item.path));
      });
  };

  result.push({
    ...mainRouters,
    displayPath: [mainRouters.name],
    relationshipNote: "Main Catalyst navigation."
  });
  usedPaths.add(featurePathKey(mainRouters.path));

  const mainRouterChildren = items.filter((item) => item.path.length === 2 && item.path[0] === mainRouters.name);
  const profilesRoot = items.find((item) => item.path.length === 1 && item.name === "Profiles");
  const emissionsDashboardRoot = items.find((item) => item.path.length === 1 && item.name === "Emission Dashboard");
  const projectEstimationsRoot = items.find((item) => item.path.length === 1 && item.name === "Project Estimations");
  const virtualAssistantRoot = items.find((item) => item.path.length === 1 && item.name === "Envana Virtual Assistant");
  const filterReportRoot = items
    .filter((item) => item.name === "Filter report export")
    .sort((left, right) => left.path.length - right.path.length)[0];

  mainRouterChildren.forEach((tab) => {
    appendSubtree(tab, [mainRouters.name], "Main Catalyst tab.");
    const tabDisplayPath = [mainRouters.name, tab.name];

    if (tab.name === "Dashboard") {
      appendSubtree(
        emissionsDashboardRoot,
        tabDisplayPath,
        "Feature group inside the Dashboard tab.",
        filterReportRoot ? [filterReportRoot] : []
      );
    } else if (tab.name === "Profiles") {
      appendSubtree(profilesRoot, tabDisplayPath, "Feature group inside the Profiles tab.");
    } else if (tab.name === "Footprints") {
      appendSubtree(projectEstimationsRoot, tabDisplayPath, "Feature group inside the Footprints tab.");
      appendSubtree(virtualAssistantRoot, tabDisplayPath, "Feature group inside the Footprints tab.");
      appendSubtree(filterReportRoot, tabDisplayPath, "Feature group inside the Footprints tab.");
    }
  });

  items.forEach((item) => {
    if (!usedPaths.has(featurePathKey(item.path))) {
      result.push({ ...item, displayPath: item.path });
    }
  });

  const hierarchy = result.map((item) => ({
    ...item,
    depth: item.displayPath.length - 1,
    hasChildren: result.some(
      (candidate) => candidate.displayPath.length === item.displayPath.length + 1 && pathStartsWith(candidate.displayPath, item.displayPath)
    )
  }));

  [...hierarchy]
    .sort((left, right) => right.displayPath.length - left.displayPath.length)
    .forEach((parent) => {
      const children = hierarchy.filter(
        (candidate) =>
          candidate.displayPath.length === parent.displayPath.length + 1 && pathStartsWith(candidate.displayPath, parent.displayPath)
      );
      if (children.length) {
        parent.enabled = children.some((child) => child.enabled);
        parent.defaultEnabled = children.some((child) => child.defaultEnabled);
      }
    });

  return hierarchy;
};

export const getChangedFeatureCount = (items = [], draft = {}) =>
  items.reduce((count, item) => count + (Boolean(draft[featurePathKey(item.path)]) !== Boolean(item.enabled) ? 1 : 0), 0);

export const createFeatureToggleInput = (items = [], draft = {}) =>
  items
    .filter((item) => Boolean(draft[featurePathKey(item.path)]) !== Boolean(item.enabled))
    .map((item) => ({
      path: item.path,
      enabled: Boolean(draft[featurePathKey(item.path)])
    }));

const isPathPrefix = (prefix, path) => prefix.length <= path.length && prefix.every((segment, index) => segment === path[index]);

export const getCatalystFeatureSupport = (item, items = []) => {
  if (CATALYST_DYNAMIC_FEATURE_NAMES.has(item.name)) {
    return { status: CATALYST_SUPPORT.SUPPORTED, message: null };
  }

  const hasSupportedDescendant = items.some(
    (candidate) =>
      candidate.path.length > item.path.length &&
      isPathPrefix(item.path, candidate.path) &&
      CATALYST_DYNAMIC_FEATURE_NAMES.has(candidate.name)
  );
  if (hasSupportedDescendant) {
    return {
      status: CATALYST_SUPPORT.INDIRECT,
      message: "Configuration group — Catalyst visibility is controlled by its supported child features."
    };
  }

  return {
    status: CATALYST_SUPPORT.UNSUPPORTED,
    message: "Catalyst does not currently support dynamic visibility for this feature. Saving this toggle will not affect the Catalyst app."
  };
};

export const getSynchronizedFeatureItems = (items = [], toggledItem) => {
  const toggledDisplayPath = toggledItem.displayPath || toggledItem.path;
  return items.filter((item) => {
    if (item.name !== toggledItem.name) {
      return false;
    }
    const itemDisplayPath = item.displayPath || item.path;
    const isSameItem = featurePathKey(item.path) === featurePathKey(toggledItem.path);
    const hasHierarchyRelationship = isPathPrefix(itemDisplayPath, toggledDisplayPath) || isPathPrefix(toggledDisplayPath, itemDisplayPath);
    return isSameItem || !hasHierarchyRelationship;
  });
};

export const toggleFeatureState = (items = [], draft = {}, toggledItem) => {
  const toggledKey = featurePathKey(toggledItem.path);
  const nextEnabled = !Boolean(draft[toggledKey]);
  const nextDraft = { ...draft, [toggledKey]: nextEnabled };
  const matchingFeatureRoots = getSynchronizedFeatureItems(items, toggledItem);

  items.forEach((item) => {
    const itemKey = featurePathKey(item.path);
    const itemDisplayPath = item.displayPath || item.path;
    const isMatchingFeatureOrDescendant = matchingFeatureRoots.some((root) => isPathPrefix(root.displayPath || root.path, itemDisplayPath));
    const isAncestorOfMatchingFeature = matchingFeatureRoots.some((root) => isPathPrefix(itemDisplayPath, root.displayPath || root.path));

    if (isMatchingFeatureOrDescendant) {
      nextDraft[itemKey] = nextEnabled;
    } else if (nextEnabled && isAncestorOfMatchingFeature) {
      nextDraft[itemKey] = true;
    }
  });

  [...items]
    .sort((left, right) => (right.displayPath || right.path).length - (left.displayPath || left.path).length)
    .forEach((parent) => {
      const parentDisplayPath = parent.displayPath || parent.path;
      const children = items.filter((candidate) => {
        const childDisplayPath = candidate.displayPath || candidate.path;
        return childDisplayPath.length === parentDisplayPath.length + 1 && isPathPrefix(parentDisplayPath, childDisplayPath);
      });
      if (children.length) {
        nextDraft[featurePathKey(parent.path)] = children.some((child) => nextDraft[featurePathKey(child.path)]);
      }
    });

  return nextDraft;
};

export const hasEnabledMainRouterFeature = (items = [], draft = {}) => {
  const mainRouterFeatures = items.filter((item) => {
    const displayPath = item.displayPath || item.path;
    return displayPath.length === 2 && displayPath[0] === "Main Routers";
  });
  return mainRouterFeatures.length === 0 || mainRouterFeatures.some((item) => Boolean(draft[featurePathKey(item.path)]));
};

export const wouldDisableAllMainRouterFeatures = (items = [], draft = {}, toggledItem) => {
  if (!Boolean(draft[featurePathKey(toggledItem.path)])) {
    return false;
  }
  return !hasEnabledMainRouterFeature(items, toggleFeatureState(items, draft, toggledItem));
};

export const getVisibleFeatureItems = (items = [], expanded = {}, searchTerm = "") => {
  const normalizedSearch = searchTerm.trim().toLowerCase();

  if (normalizedSearch) {
    const visiblePaths = new Set();

    items.forEach((item) => {
      const searchableText = `${item.name} ${item.path.join(" ")}`.toLowerCase();
      if (!searchableText.includes(normalizedSearch)) {
        return;
      }

      const displayPath = item.displayPath || item.path;
      for (let depth = 1; depth <= displayPath.length; depth += 1) {
        visiblePaths.add(featurePathKey(displayPath.slice(0, depth)));
      }
    });

    return items.filter((item) => visiblePaths.has(featureDisplayPathKey(item)));
  }

  return items.filter((item) => {
    const displayPath = item.displayPath || item.path;
    for (let depth = 1; depth < displayPath.length; depth += 1) {
      if (!expanded[featurePathKey(displayPath.slice(0, depth))]) {
        return false;
      }
    }
    return true;
  });
};
